'use client'

import { AlertTriangleIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { memo } from 'react'

import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible'
import { Select, SelectContent, SelectItem, SelectTrigger } from '@/components/ui/select'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { AccountTab } from '@/components/widget/account-tab'
import { ExchangeIcon } from '@/components/widget/exchange-icon'
import { InlineError } from '@/components/widget/inline-error'
import { ScrollableTabRow } from '@/components/widget/scrollable-tab-row'
import {
  formatLeverageX,
  formatLiquidationTransition,
  formatMarginUsage,
  formatTransition,
  formatUsdAmount,
  type LiquidationPreview,
  warningMessageKey,
} from '@/features/order/order-form/lib/format-order-preview'
import {
  INLINE_SELECT_CONTENT,
  INLINE_SELECT_ITEM,
  INLINE_SELECT_TRIGGER,
} from '@/features/order/order-form/lib/inline-select-styles'
import { useNumberFormat } from '@/hooks/use-number-format'
import { tradeVenueToIconKey } from '@/lib/proto/util'
import { cn } from '@/lib/utils'
import { useUserAccounts } from '@/service/account.hook'
import { PublicAccount } from '@/types/generated/common/account'
import { AccountGroupPreview } from '@/types/generated/public_api'

/** Prefixes "≈" only when the underlying value actually resolved — avoids
 * rendering the literal "≈—" for an absent/unresolved figure. */
function formatApprox(formatted: string, isApprox: boolean): string {
  return isApprox && formatted !== '—' ? `≈${formatted}` : formatted
}

interface OrderPreviewPanelProps {
  /** The account the order will actually submit to — never overridden by the
   * preview selector below, which only changes what this panel displays. */
  accountId: string
  /** This account's combined preview, or `undefined` while loading/absent.
   * The parent (order-form-summary) already looks this up using the
   * EFFECTIVE (possibly overridden) account id — see `previewedAccountId` —
   * so this panel only ever renders exactly what's handed to it. */
  group: AccountGroupPreview | undefined
  /** Estimated liquidation price(s) for this account's rows — one entry per instrument, see
   * collectLiquidationPreviews. Per row rather than per group, so it's handed in separately. */
  liquidation: LiquidationPreview[]
  /** False when the order-form ticket spans multiple accounts — the
   * account-preview-override control (tabs/dropdown) only makes sense when
   * every row shares one real account, so it's hidden entirely otherwise. */
  allowAccountOverride: boolean
  /** The account currently being previewed — `accountId` itself when no
   * override is active, otherwise the overridden account's id. Owned by the
   * parent: it's what drives which account's preview actually gets fetched
   * (see order-form-summary's `accountOverrides` state), so this panel only
   * reads it for display, never sets it locally. */
  previewedAccountId: string
  /** Called when the user picks a different account to preview under (tab
   * click or dropdown select), including picking the real account again to
   * clear the override — the parent decides what that means for its
   * override state, this panel just reports the selection. */
  onPreviewedAccountChange: (accountId: string) => void
  /** Extra classes for the outer wrapper — e.g. the parent zeroes out the
   * default top padding when this panel sits directly under its own tab
   * strip (multi-account ticket) rather than being the first thing in the
   * footer. */
  className?: string
  /** Whether the stat grid is open. Owned by the parent, whose chevron toggle sits on
   * the account-tab strip above this panel (order-form-summary). */
  isExpanded: boolean
}

// Single-line "label: value" so the grid stays compact. The current → after
// transition values (margin/leverage impact) run long enough to crowd a
// label at half panel width, so those rows opt into `small` for a smaller
// value font instead of wrapping to a second line. `truncate` + `title` is
// the safety net for the rare case that's still too long even at that size.
function Row({
  label,
  value,
  small,
  warnTitle,
}: {
  label: string
  value: string
  small?: boolean
  /** Set (to a tooltip string) when this row sums across the whole group and
   * that group is `incomplete` — i.e. the value is a PARTIAL sum, not a
   * complete one (see AccountGroupPreview.incomplete). Renders a small
   * warning icon in front of the value instead of changing its layout. */
  warnTitle?: string
}) {
  return (
    <div className="flex min-w-0 items-baseline justify-between gap-3">
      <p className="shrink-0 text-xs font-medium text-white/60">{label}</p>
      {warnTitle ? (
        <p
          className={cn(
            'flex min-w-0 items-center gap-1 font-medium text-white',
            small ? 'text-xs' : 'text-sm'
          )}
        >
          <WarnTip title={warnTitle}>
            <AlertTriangleIcon size={11} className="text-chart-red" />
          </WarnTip>
          <span className="truncate">{value}</span>
        </p>
      ) : (
        <p
          className={cn('truncate font-medium text-white', small ? 'text-xs' : 'text-sm')}
          title={value}
        >
          {value}
        </p>
      )}
    </div>
  )
}

/** A warning icon whose explanation opens on hover, focus, or a tap — a native `title`
 * never shows on a touch screen, which left phone users with an unexplained red triangle. */
function WarnTip({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span tabIndex={0} aria-label={title} className="inline-flex shrink-0 cursor-help">
          {children}
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-64">{title}</TooltipContent>
    </Tooltip>
  )
}

/** Space-efficient equivalent of the shared AccountTab row for 4+ accounts. Renders
 * *inside* the bordered content box's header row rather than above it. Built
 * on the shared Select component with the same INLINE_SELECT_* treatment as
 * unified-order-form's per-exchange account filter — the two are the app's
 * only account-picking dropdowns and are meant to look and behave like the
 * same control, not two independently-tuned one-offs. The currently-selected
 * account is deliberately excluded from the list below — it's already shown
 * in the trigger, so repeating it as a selectable row was both redundant and
 * the source of a rendering glitch. */
function AccountPreviewDropdown({
  accounts,
  selectedAccountId,
  realAccountId,
  onSelect,
}: {
  accounts: PublicAccount[]
  selectedAccountId: string
  realAccountId: string
  onSelect: (accountId: string) => void
}) {
  const selectedAccount = accounts.find((a) => a.accountId === selectedAccountId)
  const otherAccounts = accounts.filter((a) => a.accountId !== selectedAccountId)
  return (
    <Select value={selectedAccountId} onValueChange={onSelect}>
      <SelectTrigger hideIcon className={cn(INLINE_SELECT_TRIGGER, 'max-w-28')}>
        <div className="flex min-w-0 items-center gap-1.5">
          {selectedAccount && (
            <ExchangeIcon
              exchange={tradeVenueToIconKey(selectedAccount.tradeVenue)}
              className="size-3.5 shrink-0 rounded-full"
            />
          )}
          <span className="min-w-0 truncate">{selectedAccount?.name ?? ''}</span>
        </div>
      </SelectTrigger>
      <SelectContent className={INLINE_SELECT_CONTENT}>
        {otherAccounts.map((account) => (
          <SelectItem
            key={account.accountId}
            value={account.accountId}
            showCheck
            className={INLINE_SELECT_ITEM}
          >
            <div className="flex items-center gap-1.5">
              <ExchangeIcon
                exchange={tradeVenueToIconKey(account.tradeVenue)}
                className="size-3.5 shrink-0 rounded-full"
              />
              <span className="truncate">{account.name}</span>
              {account.accountId === realAccountId && (
                <span className="text-chart-mint shrink-0 text-[8px]">●</span>
              )}
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

function OrderPreviewPanel({
  accountId,
  group,
  liquidation,
  allowAccountOverride,
  previewedAccountId,
  onPreviewedAccountChange,
  className,
  isExpanded,
}: OrderPreviewPanelProps) {
  const t = useTranslations('orderForm')
  const fmt = useNumberFormat()

  const { data: userAccountsData } = useUserAccounts()
  const allAccounts = userAccountsData?.accounts ?? []
  // Only the real submitting account — the picker used to offer every other
  // account on the same trade venue (e.g. "Hyperliquid Alex" alongside
  // "Hyperliquid") to preview under, but that surfaced accounts the user
  // never selected for this order. Restricting to just accountId collapses
  // showTabs/showDropdown below to a no-op, so the panel always shows the
  // account actually chosen for this leg.
  const realAccount = allAccounts.find((a) => a.accountId === accountId)
  const previewableAccounts = realAccount ? [realAccount] : allAccounts

  const effectiveAccountId = previewedAccountId

  // `group.incomplete` means at least one row in the batch failed to price
  // (bad instrument, invalid quantity, unresolved USD rate) — so every
  // group-derived sum below (headline value/fees, the pool margin rows, and
  // the leverage transitions) is a PARTIAL total, not a complete one. Computed
  // ahead of marginRows so it can be applied there too.
  const incompleteTitle = group?.incomplete ? t('previewIncomplete') : undefined

  // Margin is pool-scoped: a group spanning 2+ margin pools (e.g. multiple
  // Hyperliquid HIP-3 dexes) must show one Margin Required/Available/Impact
  // sub-block per pool rather than blending them into a single row — see
  // design §2.2. Single-pool groups keep the flat single-row layout.
  const singlePool = group?.pools.length === 1 ? group.pools[0] : undefined
  const marginRows: Array<{ label: string; value: string; small?: boolean; warnTitle?: string }> =
    group && group.pools.length > 1
      ? group.pools.flatMap((pool) => [
          {
            label: `${t('marginRequired')} (${pool.dexKey})`,
            value: fmt.text(formatApprox(formatUsdAmount(pool.marginRequired), pool.approximate), {
              sensitive: true,
            }),
            warnTitle: incompleteTitle,
          },
          {
            label: `${t('marginAvailable')} (${pool.dexKey})`,
            value: fmt.text(
              formatTransition(
                pool.marginAvailableCurrent,
                pool.marginAvailableAfter,
                formatUsdAmount
              ),
              { sensitive: true }
            ),
            small: true,
            warnTitle: incompleteTitle,
          },
          {
            label: `${t('marginImpact')} (${pool.dexKey})`,
            value: formatMarginUsage(
              pool.marginUsedPctCurrent,
              pool.marginUsedPctAfter,
              pool.marginImpactPct
            ),
            small: true,
            warnTitle: incompleteTitle,
          },
        ])
      : [
          {
            label: t('marginRequired'),
            value: fmt.text(
              formatApprox(formatUsdAmount(singlePool?.marginRequired), !!singlePool?.approximate),
              { sensitive: true }
            ),
            warnTitle: incompleteTitle,
          },
          {
            label: t('marginImpact'),
            value: formatMarginUsage(
              singlePool?.marginUsedPctCurrent,
              singlePool?.marginUsedPctAfter,
              singlePool?.marginImpactPct
            ),
            small: true,
            warnTitle: incompleteTitle,
          },
        ]

  // Single-pool only: Margin Available's own current → after transition (two full USD
  // amounts) is the longest value in this grid and keeps truncating at half panel width
  // even at `small` (see the Row comment above). Moved out of marginRows and appended at
  // the very end of `rows` below (swapped with Approx. Fees, which is short and never
  // truncates) so it lands alone in the grid's last row, where the col-span-2 rule on the
  // grid container gives it the full row instead of a half-width column. Multi-pool groups
  // keep their per-pool Margin Available inline in marginRows above — there can be more
  // than one, so there's no single slot to swap it into.
  const singlePoolMarginAvailableRow =
    group && group.pools.length > 1
      ? undefined
      : {
          label: t('marginAvailable'),
          value: fmt.text(
            formatTransition(
              singlePool?.marginAvailableCurrent,
              singlePool?.marginAvailableAfter,
              formatUsdAmount
            ),
            { sensitive: true }
          ),
          small: true,
          warnTitle: incompleteTitle,
        }

  // `small` marks the current → after transition rows: their values run long
  // enough to threaten truncation at half panel width, so they render at a
  // smaller font instead of wrapping to a second line (see Row above).
  const rows: Array<{ label: string; value: string; small?: boolean; warnTitle?: string }> = [
    {
      label: t('totalValue'),
      value: formatUsdAmount(group?.totalValueUsd),
      warnTitle: incompleteTitle,
    },
    ...marginRows,
    {
      label: t('grossLeverage'),
      value: formatTransition(
        group?.grossLeverageCurrent,
        group?.grossLeverageAfter,
        formatLeverageX
      ),
      small: true,
      warnTitle: incompleteTitle,
    },
    {
      label: t('netLeverage'),
      value: formatTransition(group?.netLeverageCurrent, group?.netLeverageAfter, formatLeverageX),
      small: true,
      warnTitle: incompleteTitle,
    },
    {
      label: t('approxFees'),
      value: formatApprox(formatUsdAmount(group?.feeUsd), true),
      warnTitle: incompleteTitle,
    },
    ...(singlePoolMarginAvailableRow ? [singlePoolMarginAvailableRow] : []),
  ]

  // A price, so unmarked (not `sensitive`). Full width below the grid: the current → after
  // pair is as long as Margin Available's, and a symbol-labelled row (2+ instruments) longer.
  const liquidationRows = liquidation.map((entry) => ({
    label: entry.symbol ? `${t('liquidationPrice')} (${entry.symbol})` : t('liquidationPrice'),
    value: fmt.text(formatLiquidationTransition(entry.current, entry.after, entry.approximate)),
  }))

  // Warning codes are pool-scoped; surface the first pool that has one
  // rather than picking a single pool arbitrarily — there's no severity
  // ranking on MarginPoolPreview to break ties by.
  const warningKey = warningMessageKey(group?.pools.find((pool) => pool.warningCode)?.warningCode)

  // previewableAccounts is now always just the real account (see above), so
  // a tab row here would only ever render one single, non-switchable tab —
  // strictly worse than not showing it at all, since there's nothing to
  // switch to. The dropdown variant (4+ accounts) can never apply anymore
  // either.
  const showTabs = allowAccountOverride && previewableAccounts.length > 1
  const showDropdown = false

  return (
    // The tab row and the bordered content box below are siblings, not
    // nested, and touch with zero *vertical* gap on purpose: that's what
    // lets the active tab's -mb-px (see AccountTab) overlap exactly
    // onto the box's top border. This row also carries zero *horizontal*
    // inset (no px-4) so its left/right edges line up with the box's own
    // border below rather than the box's inset content — any inset here
    // would leave a strip of unmasked border showing instead of the mask
    // reaching all the way to the box's corner. The dropdown variant (4+
    // accounts) doesn't use this merge illusion at all — it renders inside
    // the box's own header row instead, see below.
    <div className={cn('flex flex-col pt-5', className)}>
      {showTabs && (
        // relative z-10 here (not on the individual tab) is what actually lifts
        // the overlap above the content box: the box is a later, unpositioned
        // sibling of *this* div, so it always paints on top in normal stacking
        // order unless this whole row is promoted to its own stacking layer.
        <ScrollableTabRow
          className="z-10"
          contentClassName="items-end! justify-start gap-1"
          fadeClassName="from-card"
        >
          {previewableAccounts.map((account) => {
            const isRealAccount = account.accountId === accountId
            return (
              <AccountTab
                key={account.accountId}
                account={account}
                isActive={account.accountId === effectiveAccountId}
                title={isRealAccount ? t('previewRealAccountHint') : undefined}
                accent={
                  isRealAccount && <span className="text-chart-mint shrink-0 text-[8px]">●</span>
                }
                onSelect={() => onPreviewedAccountChange(account.accountId)}
              />
            )
          })}
        </ScrollableTabRow>
      )}
      <Collapsible open={isExpanded} className="border-border flex flex-col">
        {/* Header: houses the account-preview dropdown (4+ accounts only) and,
            when collapsed, a one-line stand-in for the full stat grid below.
            Skipped while expanded with no dropdown, since the same figure
            already appears as the first row of the grid. */}
        {(showDropdown || !isExpanded) && (
          <div className="flex items-center gap-2 px-4 py-2">
            {showDropdown && (
              <AccountPreviewDropdown
                accounts={previewableAccounts}
                selectedAccountId={effectiveAccountId}
                realAccountId={accountId}
                onSelect={(id) => onPreviewedAccountChange(id)}
              />
            )}
            {!isExpanded && (
              <span className="flex min-w-0 items-baseline gap-1.5 truncate">
                <span className="text-sm font-medium text-white/60">{t('totalValue')}</span>
                <span className="text-sm font-medium text-white">
                  {formatUsdAmount(group?.totalValueUsd)}
                </span>
                {incompleteTitle && (
                  <WarnTip title={incompleteTitle}>
                    <AlertTriangleIcon size={12} className="text-chart-red" />
                  </WarnTip>
                )}
              </span>
            )}
          </div>
        )}
        <CollapsibleContent>
          {/* A trailing unpaired row (odd total row count) spans both columns instead of
              sitting alone in the left half — the whole point of ending `rows` on
              Margin Available (see singlePoolMarginAvailableRow above) is giving its long
              current → after value the full row width instead of truncating at half. */}
          <div className="flex flex-col gap-y-1 px-6 py-4">
            <div className="[&>*:nth-child(even)]:border-border/40 grid grid-cols-2 gap-x-4 gap-y-1 [&>*:last-child:nth-child(odd)]:col-span-2 [&>*:nth-child(even)]:border-l [&>*:nth-child(even)]:pl-3">
              {rows.map((row) => (
                <Row
                  key={row.label}
                  label={row.label}
                  value={row.value}
                  small={row.small}
                  warnTitle={row.warnTitle}
                />
              ))}
            </div>
            {liquidationRows.map((row) => (
              <Row key={row.label} label={row.label} value={row.value} small />
            ))}
          </div>
        </CollapsibleContent>
        {/* Deliberately outside CollapsibleContent: a margin/liquidation
            warning shouldn't disappear just because the stat grid is
            collapsed. */}
        {warningKey && (
          <div className="flex flex-col gap-1.5 px-4 pb-3">
            <InlineError>{t(warningKey)}</InlineError>
          </div>
        )}
      </Collapsible>
    </div>
  )
}

// Every account's panel would otherwise fully re-render/reformat on every
// keystroke anywhere in the form (the parent re-renders on essentially every
// form.watch() change) even when this account's own `group` data hasn't
// changed — memo() skips that as long as the parent also hands this a
// stable onPreviewedAccountChange reference (see order-form-summary.tsx's
// previewedAccountChangeHandlers).
export default memo(OrderPreviewPanel)
